/**
 * SCI Field QC App — seed data.
 *
 * Everything in this file is a VERBATIM transcription from the design spec
 * (docs/superpowers/specs/2026-07-28-sci-field-qc-app-design.md), Appendices
 * A, B and C. These are the actual contents of a live QA document: the item
 * numbers, the wording and the point types are evidentiary. Do not paraphrase,
 * reorder, shorten or "improve" any string in this file. If the paper document
 * changes, change it here and nowhere else.
 *
 * Relationship to ./types.ts
 * --------------------------
 * `types.ts` carries an ASCII-folded copy of the three item lists so the wire
 * contract is self-contained. Where the two disagree, THIS FILE IS
 * AUTHORITATIVE — it is the transcription. The only known difference today is
 * end-of-pour item 9: '3 m³' here, '3 m3' there. Seeding code should import
 * from this module.
 *
 * What seeds what
 * ---------------
 *   JOB_SETUP_ITEMS      -> job_setup_item, once per project  (also in seed.sql)
 *   POUR_ITP_ITEMS       -> itp_item,        once per pour
 *   END_OF_POUR_ITEMS    -> end_of_pour_item, once per pour
 *   CALIBRATION_PROCEDURE -> not a table; shown in-app as on-screen guidance
 *   CHUTE_TEST_POINT_OPTIONS -> the `test_point` picker
 *   SEED_PROJECT         -> mirrors the single project row in seed.sql
 *   EQUIPMENT_CHECKLIST  -> equipment_inspection_item, once per new inspection
 *   DAILY_REPORT_DEFAULT_DELIVERIES -> daily_report_delivery, once per new report
 *
 * A note on EQUIPMENT_CHECKLIST specifically, because it is a DELIBERATE
 * exception to the "verbatim, do not improve" rule above: the paper's printed
 * wording carries seven typos ("Batter Cable", "Guages", "Roll Over
 * Production", "Sea Belt/Seat", "Explaination", "House" for the engine
 * compartment's hoses, "Tempuature"). Geoff approved correcting them
 * explicitly, because a safety checklist item that reads "Roll Over
 * Production" is a defect in the paper, not a phrase worth preserving. Each
 * correction still records the printed wording beside it via `printedAs`, so
 * the transcription stays traceable — the correction is not a paraphrase,
 * it is a recorded correction.
 */

import type {
  ChuteTestPoint,
  EndOfPourStatus,
  EquipmentChecklistGroup,
  ItpStatus,
  MixMaterialUnit,
  PointType,
  SeedItem
} from './types';
import { CHUTE_TEST_POINT_LABELS } from './types';

export type { SeedItem };

/* ==========================================================================
 * Appendix A — ITP items
 *
 * "H = Hold, W = Witness, S = Surveillance, R = Review."
 * Combinations are slash-joined exactly as the paper writes them ('H/R').
 * Anything containing 'H' is a Hold point and is subject to the blocking rule.
 * ======================================================================== */

/** The point-type legend, verbatim from Appendix A. */
export const POINT_TYPE_LABELS: Record<string, string> = {
  H: 'Hold',
  W: 'Witness',
  S: 'Surveillance',
  R: 'Review'
};

/** Expand a point type such as 'H/R' to 'Hold / Review'. */
export function describePointType(pointType: PointType): string {
  return pointType
    .split('/')
    .map((part) => POINT_TYPE_LABELS[part.trim()] ?? part.trim())
    .join(' / ');
}

/** True when the item is a Hold point (its point type contains 'H'). */
export function isHoldPoint(pointType: PointType): boolean {
  return pointType
    .split('/')
    .some((part) => part.trim().toUpperCase() === 'H');
}

/**
 * ITP items 1–5 — job setup, one set per project.
 * Appendix A: "Job setup (1–5): pre-job quality review `H`; personnel
 * readiness / training confirmation `H/R`; material documentation `H/R`;
 * manufacturer pump/application confirmation `H/R`; water source documentation
 * `H/R`."
 */
export const JOB_SETUP_ITEMS: readonly SeedItem[] = [
  { item_no: 1, title: 'Pre-job quality review', point_type: 'H' },
  { item_no: 2, title: 'Personnel readiness / training confirmation', point_type: 'H/R' },
  { item_no: 3, title: 'Material documentation', point_type: 'H/R' },
  { item_no: 4, title: 'Manufacturer pump/application confirmation', point_type: 'H/R' },
  { item_no: 5, title: 'Water source documentation', point_type: 'H/R' }
] as const;

/**
 * ITP items 6–31 — per pour, one set per pour record.
 * Appendix A, "Per pour (6–31)", in the order the paper lists them.
 */
export const POUR_ITP_ITEMS: readonly SeedItem[] = [
  { item_no: 6, title: 'Material supply and loading', point_type: 'S' },
  { item_no: 7, title: 'Grout lot/batch tracking', point_type: 'R' },
  { item_no: 8, title: 'Equipment cleanout / dedication', point_type: 'W' },
  { item_no: 9, title: 'Equipment inspection', point_type: 'S' },
  { item_no: 10, title: 'Pump/line setup confirmed', point_type: 'H/W' },
  { item_no: 11, title: 'Final acceptance point confirmed', point_type: 'H/R' },
  { item_no: 12, title: 'Pre-pour dry grout feed calibration', point_type: 'H' },
  { item_no: 13, title: 'Pre-pour water flow meter verification', point_type: 'H' },
  { item_no: 14, title: 'Approved production rate confirmed', point_type: 'H/R' },
  { item_no: 15, title: 'Dry feed and water input stability', point_type: 'H/S' },
  { item_no: 16, title: 'Trial/start-up discharge', point_type: 'H' },
  { item_no: 17, title: 'Initial chute test', point_type: 'H/W' },
  { item_no: 18, title: 'Interval chute testing', point_type: 'W/S' },
  { item_no: 19, title: 'Additional testing after changes', point_type: 'H/W' },
  { item_no: 20, title: 'Temperature monitoring', point_type: 'S' },
  { item_no: 21, title: 'Material feed and water control monitoring', point_type: 'S' },
  { item_no: 22, title: 'Continuous flow / pump coordination', point_type: 'S' },
  { item_no: 23, title: 'Water dosage monitoring', point_type: 'S/R' },
  { item_no: 24, title: 'Volume tracking / uniform yield record', point_type: 'R' },
  { item_no: 25, title: 'Stop-hold action plan', point_type: 'H' },
  { item_no: 26, title: 'Controlled adjustment record', point_type: 'R' },
  { item_no: 27, title: 'Restart after hold/stop', point_type: 'H' },
  { item_no: 28, title: 'Production ticket / grout log', point_type: 'R' },
  { item_no: 29, title: 'Cleanout', point_type: 'S' },
  { item_no: 30, title: 'Document submission timing confirmed', point_type: 'R' },
  { item_no: 31, title: 'Final documentation review', point_type: 'R' }
] as const;

/** All 31 ITP items in document order. Convenience for exports and printing. */
export const ALL_ITP_ITEMS: readonly SeedItem[] = [
  ...JOB_SETUP_ITEMS,
  ...POUR_ITP_ITEMS
] as const;

/** Status every seeded ITP / job-setup row starts in. */
export const ITP_ITEM_SEED_STATUS: ItpStatus = 'pending';

/* ==========================================================================
 * Appendix B — end-of-pour checklist
 * The 14 Yes / No / N-A items, in the order the paper lists them.
 * ======================================================================== */

export interface EndOfPourSeedItem {
  /** 1..14 */
  item_no: number;
  title: string;
}

export const END_OF_POUR_ITEMS: readonly EndOfPourSeedItem[] = [
  { item_no: 1, title: 'Pre-job quality review completed' },
  { item_no: 2, title: 'Personnel readiness/training confirmed' },
  { item_no: 3, title: 'Material documentation received/accepted' },
  { item_no: 4, title: 'Potable water documentation received/accepted' },
  { item_no: 5, title: 'Material lot/batch tracking completed' },
  { item_no: 6, title: 'Pump/line setup and acceptance point confirmed' },
  { item_no: 7, title: 'Pre-pour calibration completed and documented' },
  {
    item_no: 8,
    title: 'Initial chute/acceptance-point test completed by Kiewit QC/tester'
  },
  { item_no: 9, title: 'Interval tests completed every 3 m³ or as directed' },
  { item_no: 10, title: 'All water/feed/production adjustments documented' },
  { item_no: 11, title: 'All hold/stop events documented and closed' },
  { item_no: 12, title: 'Volume tracking / grout log completed' },
  { item_no: 13, title: 'SCI equipment cleanout completed before grout set' },
  { item_no: 14, title: 'Records provided to Kiewit/QC for review' }
] as const;

/** Status every seeded end-of-pour row starts in. */
export const END_OF_POUR_SEED_STATUS: EndOfPourStatus = 'pending';

/* ==========================================================================
 * Chute test — `test_point` options
 *
 * Spec, Chute tests: "test_point (initial/start-up · interval · after
 * adjustment · after stop/restart · visible change · final)".
 * Labels come from CHUTE_TEST_POINT_LABELS in types.ts so the picker, the
 * exports and the wire contract can never drift apart.
 * ======================================================================== */

export interface ChuteTestPointOption {
  value: ChuteTestPoint;
  label: string;
}

/** In pour order: the first test, the repeating ones, then the last. */
export const CHUTE_TEST_POINT_ORDER: readonly ChuteTestPoint[] = [
  'initial',
  'interval',
  'after_adjustment',
  'after_stop_restart',
  'visible_change',
  'final'
] as const;

export const CHUTE_TEST_POINT_OPTIONS: readonly ChuteTestPointOption[] =
  CHUTE_TEST_POINT_ORDER.map((value) => ({
    value,
    label: CHUTE_TEST_POINT_LABELS[value]
  }));

/** Default selection when a crew member adds a row mid-pour. */
export const CHUTE_TEST_POINT_DEFAULT: ChuteTestPoint = 'interval';

/* ==========================================================================
 * Appendix C — calibration procedure
 *
 * Transcribed verbatim from the existing worksheet, for display in-app beside
 * the calibration screen. This is guidance text only; it is not stored in D1.
 * ======================================================================== */

export interface CalibrationProcedureStep {
  /** 1..10 */
  step: number;
  text: string;
}

export const CALIBRATION_PROCEDURE: readonly CalibrationProcedureStep[] = [
  {
    step: 1,
    text: 'Start the volumetric mixer and prime grout into the auger; dispose of priming grout.'
  },
  { step: 2, text: 'Put an empty bucket on the scale and zero it.' },
  { step: 3, text: 'Discharge 50–65 kg into the bucket.' },
  { step: 4, text: 'Obtain weight and record the result.' },
  {
    step: 5,
    text: 'Photograph the batch computer screen for counts/kg and number of counts.'
  },
  { step: 6, text: 'Calculate counts/kg × actual counts.' },
  { step: 7, text: 'Back-check: actual weight ÷ actual counts.' },
  { step: 8, text: 'Check water: litres dispensed = weight in kg, expect ±1%.' },
  {
    step: 9,
    text: 'Check spread: target 16–19 in. Below 16, add water; above 19, reduce water dosage rate.'
  },
  {
    step: 10,
    text: 'Validate with flow: target 40–70 s. Below 40, too much water; above 70, not enough.'
  }
] as const;

/** Number of steps in the printed procedure. Guards against a lost line. */
export const CALIBRATION_PROCEDURE_STEP_COUNT = 10;

/* ==========================================================================
 * Seed project — Kiewit Woodfibre FST
 *
 * Mirrors the single project row inserted by seed.sql. Kept here so tests and
 * local tooling do not have to parse SQL.
 * ======================================================================== */

/**
 * Placeholder password hash written by seed.sql.
 *
 * It is deliberately NOT a valid scrypt hash: it has no salt, no parameters
 * and no derived key, so any correct verifier fails to parse it and the login
 * fails closed. REPLACE BOTH HASHES WITH REAL scrypt HASHES BEFORE THE
 * PROJECT IS USED ON SITE.
 */
export const PLACEHOLDER_PASSWORD_HASH =
  'REPLACE_ME__not_a_valid_scrypt_hash__set_before_use';

/**
 * True when a stored hash is still the seed placeholder. Auth MUST refuse to
 * authenticate against it rather than attempt a comparison.
 */
export function isPlaceholderPasswordHash(hash: string | null | undefined): boolean {
  return typeof hash === 'string' && hash.startsWith('REPLACE_ME__');
}

export interface SeedProject {
  code: string;
  name: string;
  client: string;
  product: string;
  /** Not supplied by the job paperwork yet; set from the field. */
  sci_unit_no: string | null;
  /** Not supplied by the job paperwork yet; set from the field. */
  acceptance_point: string | null;
  crew_password_hash: string;
  client_password_hash: string;
  archived: 0 | 1;
}

/** The one project the app ships with. Code is matched case-insensitively. */
export const SEED_PROJECT: SeedProject = {
  code: 'KiewitWF',
  name: 'Kiewit Woodfibre FST',
  client: 'Kiewit',
  product: '5 Star U/W H/S Grout',
  sci_unit_no: null,
  acceptance_point: null,
  crew_password_hash: PLACEHOLDER_PASSWORD_HASH,
  client_password_hash: PLACEHOLDER_PASSWORD_HASH,
  archived: 0
};

/** Every project ships with exactly this list. */
export const SEED_PROJECTS: readonly SeedProject[] = [SEED_PROJECT] as const;

/* ==========================================================================
 * The quality area — Equipment Safety Inspection checklist and Daily Report
 * defaults, transcribed from `1000163458.jpg` and `1000163459.jpg`.
 *
 * See the file header for why EQUIPMENT_CHECKLIST's corrected wording is a
 * deliberate, Geoff-approved departure from this file's verbatim rule, with
 * the printed wording kept beside each correction via `printedAs`.
 * ======================================================================== */

/** Display titles for the five printed checklist groups, in printed order. */
export const EQUIPMENT_CHECKLIST_GROUP_LABELS: Record<EquipmentChecklistGroup, string> = {
  outside: 'Outside',
  engine: 'Engine Compartment',
  cab: 'Inside Cab',
  cab_continued: 'Inside Cab (continued)',
  fluids: 'Fluids'
};

/** The printed legend: 'N/A = Not Applicable', 'OK = No Repairs Needed', 'RR = Requires Repair'. */
export const EQUIPMENT_CHECKLIST_LEGEND =
  'N/A = Not Applicable · OK = No Repairs Needed · RR = Requires Repair';

export interface EquipmentChecklistItem {
  group: EquipmentChecklistGroup;
  label: string;
  /** The printed wording, when it differs. Kept so the transcription is traceable. */
  printedAs?: string;
}

/**
 * The full printed checklist, in printed order, grouped. Seeded into
 * equipment_inspection_item (status 'pending') whenever a new inspection is
 * created with no `items` supplied.
 */
export const EQUIPMENT_CHECKLIST: readonly EquipmentChecklistItem[] = [
  // outside
  { group: 'outside', label: 'Lights' },
  { group: 'outside', label: 'Steps/Hand Rails' },
  { group: 'outside', label: 'Tires/Tracks' },
  { group: 'outside', label: 'Exhaust' },
  { group: 'outside', label: 'Fenders' },
  { group: 'outside', label: 'Bucket' },
  { group: 'outside', label: 'Cutting Edge/Torch' },
  { group: 'outside', label: 'Lifting Mechanism' },
  { group: 'outside', label: 'Hoses' },
  { group: 'outside', label: 'Fittings Greased' },
  { group: 'outside', label: 'Hatch Coupler' },
  { group: 'outside', label: 'Wipers' },
  // engine
  { group: 'engine', label: 'Battery Cable', printedAs: 'Batter Cable' },
  { group: 'engine', label: 'Fan Belt' },
  { group: 'engine', label: 'Hoses', printedAs: 'House' },
  { group: 'engine', label: 'Air Filter' },
  { group: 'engine', label: 'Guards' },
  // cab
  { group: 'cab', label: 'Brakes Service' },
  { group: 'cab', label: 'Brakes Parking' },
  { group: 'cab', label: 'Backup Alarm' },
  { group: 'cab', label: 'Fire Extinguisher' },
  { group: 'cab', label: 'Gauges', printedAs: 'Guages' },
  { group: 'cab', label: 'Hose' },
  { group: 'cab', label: 'Hydraulic Controls' },
  // cab_continued
  { group: 'cab_continued', label: 'Glass (all sides)' },
  { group: 'cab_continued', label: 'Mirror' },
  { group: 'cab_continued', label: 'Roll Over Protection', printedAs: 'Roll Over Production' },
  { group: 'cab_continued', label: 'Seat Belt/Seat', printedAs: 'Sea Belt/Seat' },
  { group: 'cab_continued', label: 'Steering' },
  // fluids
  { group: 'fluids', label: 'Visible leaks' },
  { group: 'fluids', label: 'Oil Level/Pressure' },
  { group: 'fluids', label: 'Coolant Level (check only when equipment is COLD)' },
  { group: 'fluids', label: 'Hydraulic Oil Level' },
  { group: 'fluids', label: 'Transmission Fluid Level' },
  { group: 'fluids', label: 'Fuel Level' }
] as const;

/**
 * Delivery materials a new Daily Report starts with. All rows are
 * renameable and removable.
 */
export const DAILY_REPORT_DEFAULT_DELIVERIES: readonly string[] = [
  'Sand',
  'Gravel',
  'Cement',
  // One Admixture row (2026-11-03; was four). Add more on the form if needed.
  'Admixture'
] as const;

/* ==========================================================================
 * Counts — asserted by the seed tests so a dropped line is caught immediately
 * ======================================================================== */

export const SEED_COUNTS = {
  job_setup_items: JOB_SETUP_ITEMS.length,
  pour_itp_items: POUR_ITP_ITEMS.length,
  all_itp_items: ALL_ITP_ITEMS.length,
  end_of_pour_items: END_OF_POUR_ITEMS.length,
  chute_test_point_options: CHUTE_TEST_POINT_OPTIONS.length,
  calibration_procedure_steps: CALIBRATION_PROCEDURE.length,
  projects: SEED_PROJECTS.length,
  equipment_checklist_items: EQUIPMENT_CHECKLIST.length,
  daily_report_default_deliveries: DAILY_REPORT_DEFAULT_DELIVERIES.length
} as const;

/** Expected counts, hard-coded from the paper document. */
export const EXPECTED_SEED_COUNTS = {
  job_setup_items: 5,
  pour_itp_items: 26,
  all_itp_items: 31,
  end_of_pour_items: 14,
  chute_test_point_options: 6,
  calibration_procedure_steps: 10,
  projects: 1,
  equipment_checklist_items: 35,
  daily_report_default_deliveries: 4
} as const;

/* ==========================================================================
 * Mix design — the standard materials rows
 *
 * Transcribed from the 5 MPa Secant Pile Wall sheet (mix code CC520N, revised
 * 4 July 2025). A new mix design starts with exactly these rows; the author
 * renames, adds and removes them from there, so a mix with fly ash or a
 * different admixture needs no code change.
 * ======================================================================== */

/** One starting row. `amount` is blank: the author fills the figures in. */
export interface MixDesignSeedMaterial {
  name: string;
  unit: MixMaterialUnit;
}

export const MIX_DESIGN_DEFAULT_MATERIALS: readonly MixDesignSeedMaterial[] = [
  { name: 'Cement. (Type GUL)', unit: 'kg' },
  { name: 'CSA 20mmx10mm Natural Agg (20-10mm) (SSD) Pipeline Heidelberg Materials', unit: 'kg' },
  { name: 'Fine Aggregate (SSD) Pipeline Heidelberg Materials', unit: 'kg' },
  { name: 'Target Water', unit: 'L' },
  { name: 'High Range Water Reducer. Dynamon-SX', unit: 'mL' },
  { name: 'Hydration Stabilization Mapetard SD 190', unit: 'mL' },
  { name: 'Air Content', unit: '%' }
] as const;
